import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  ArrowLeft,
  Wallet,
  Sparkles
} from 'lucide-react'

const PerkswallPage: React.FC = () => {
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 24, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  }

  return (
    <div className="h-screen flex flex-col bg-[#F8F9FA] overflow-hidden no-scroll-page" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Fixed Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link
              to="/"
              className="inline-flex items-center space-x-2 text-[#6B7280] hover:text-[#1A2B49] transition-colors duration-200 font-medium"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </Link>
            
            <div className="hidden sm:flex items-center space-x-1">
              <div className="w-2 h-2 bg-[#FE4215] rounded-full"></div>
              <div className="w-2 h-2 bg-[#E5E7EB] rounded-full"></div>
              <div className="w-2 h-2 bg-[#E5E7EB] rounded-full"></div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content Container */}
      <div className="flex-1 pt-16 overflow-hidden">
        {/* Hero Section - Full Screen */}
        <section className="relative h-[calc(100vh-4rem)] flex items-center justify-center overflow-hidden">
          {/* Background Elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-[#FE4215]/10 to-[#E53910]/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-[#E53910]/10 to-[#FE4215]/10 rounded-full blur-3xl"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="text-center w-full scale-100 sm:scale-100 xs:scale-95 2xs:scale-90"
            >
            {/* Logo/Icon */}
            <motion.div variants={itemVariants} className="mb-6 sm:mb-8 md:mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-gradient-to-r from-[#FE4215] to-[#E53910] rounded-2xl shadow-lg">
                <Wallet className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-white" />
              </div>
            </motion.div>              {/* Main Heading */}
              <motion.h1
                variants={itemVariants}
                className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-[#1A2B49] mb-6 sm:mb-8 md:mb-12 tracking-tight"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Perks
                <span className="bg-gradient-to-r from-[#FE4215] to-[#E53910] bg-clip-text text-transparent">
                  Wall
                </span>
              </motion.h1>
              
              {/* Subtitle */}
              <motion.p
                variants={itemVariants}
                className="text-xl sm:text-2xl lg:text-3xl text-[#6B7280] max-w-5xl mx-auto leading-relaxed mb-10 sm:mb-12"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Display multiple offers and perks in a customizable, branded interface
                that <span className="font-semibold text-[#1A2B49]">rewards users</span> with
                points or virtual currency when they interact with offers.
              </motion.p>



            {/* Examples Section */}
            <motion.div variants={itemVariants} className="mb-8 sm:mb-12">
              <h2 className="text-3xl sm:text-4xl font-semibold text-[#1A2B49] mb-6 sm:mb-10" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Available Examples
                </h2>
                
                {/* Demo Cards Grid */}
                <div className="max-w-md mx-auto">
                  <motion.div
                    className="group cursor-pointer bg-white rounded-lg transition-all duration-300 hover:shadow-lg overflow-hidden flex flex-col"
                    style={{ 
                      boxShadow: '0 3px 10px rgba(0, 0, 0, 0.08)',
                      borderRadius: '8px',
                      minHeight: '350px'
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {/* Card Header */}
                    <div className="h-2" style={{ backgroundColor: '#FE4215' }}></div>
                    
                    {/* Card Content */}
                    <div className="p-6 sm:p-8 flex flex-col flex-grow">
                      <div className="flex items-center mb-4 sm:mb-6">
                        <div 
                          className="p-2 sm:p-3 rounded-lg mr-3 sm:mr-4 flex-shrink-0"
                          style={{ backgroundColor: '#FE4215' }}
                        >
                          <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                        </div>
                        <h3 
                          className="text-xl sm:text-2xl font-bold"
                          style={{ 
                            fontFamily: 'Inter, sans-serif',
                            color: '#1A2B49',
                            wordBreak: 'break-word'
                          }}
                        >
                          Featured Offers
                        </h3>
                      </div>

                      {/* Description */}
                      <p 
                        className="text-sm sm:text-base mb-4 flex-grow"
                        style={{ 
                          fontFamily: 'Inter, sans-serif',
                          color: '#6E6D7A',
                          lineHeight: '1.5',
                          paddingTop: '8px',
                          wordBreak: 'break-word'
                        }}
                      >
                        Showcase special and highlighted promotional offers to engage users with top deals. Enhances user experience with relevant perks and rewards.
                      </p>

                      {/* CTA Button */}
                      <Link 
                        to="/perkswall-embedded"
                        className="w-full py-3 sm:py-4 px-4 sm:px-6 rounded-lg font-bold text-white transition-all duration-300 hover:opacity-90 mt-auto"
                        style={{ 
                          backgroundColor: '#FE4215',
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '16px',
                          borderRadius: '8px',
                          boxShadow: '0 2px 5px rgba(254, 66, 21, 0.2)',
                          border: 'none',
                          marginTop: '16px',
                          whiteSpace: 'normal',
                          height: 'auto',
                          minHeight: '48px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        Preview Demo
                      </Link>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </div>
    </div>
  )
}

export default PerkswallPage